import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { X } from 'lucide-react';
import { useWorkspace } from '@/context/WorkspaceContext';

interface MobileSubMenuProps {
  isGlobalDarkMode: boolean;
  subItems: any[];
  onClose: () => void;
  parentLabel?: string;
  hasTopBar?: boolean;
}

export const MobileSubMenu: React.FC<MobileSubMenuProps> = ({ isGlobalDarkMode, subItems, onClose, parentLabel, hasTopBar = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentWorkspace } = useWorkspace();

  const handleItemClick = (path: string) => {
    navigate(path);
    onClose();
  };

  const isActivePath = (path: string) => {
    if (location.pathname === path) return true;
    if (location.pathname.startsWith(path + '/')) {
      return !subItems.some(item =>
        item.path !== path &&
        item.path.startsWith(path) &&
        location.pathname.startsWith(item.path)
      );
    }
    return false;
  };

  const borderColor = isGlobalDarkMode ? 'border-sidebar-border' : 'border-gray-200';
  const textColor = isGlobalDarkMode ? 'text-sidebar-foreground' : 'text-gray-900';

  return (
    <>
      {/* Backdrop Overlay */}
      <div
        className="fixed top-0 left-0 right-0 bottom-16 bg-black bg-opacity-50 z-40"
        onClick={onClose}
      />

      {/* Drawer Sidebar */}
      <aside
        className={`fixed left-0 top-0 w-80 max-w-[85vw] h-[calc(100vh-4rem)] ${borderColor} border-r flex flex-col z-50 ${
          isGlobalDarkMode ? 'bg-sidebar' : 'bg-sidebar-light'
        }`}
        style={!isGlobalDarkMode && currentWorkspace?.appearance?.theme?.sidebarColor
          ? { backgroundColor: currentWorkspace.appearance.theme.sidebarColor }
          : undefined}
      >
        {/* Header */}
        <div className={`px-6 py-6 ${borderColor} border-b flex items-center justify-between`}>
          <h3 className={`text-lg font-semibold ${textColor}`}>
            {parentLabel || 'Menu'}
          </h3>
          <button
            onClick={onClose}
            className={`p-2 rounded-lg transition-colors ${
              isGlobalDarkMode
                ? 'hover:bg-sidebar-accent text-sidebar-foreground/80'
                : 'hover:bg-gray-200 text-gray-600'
            }`}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content Area */}
        <nav className="flex-1 p-3 overflow-y-auto">
          <ul className="space-y-1">
            {subItems.map((subItem) => {
              const SubIcon = subItem.icon;
              const isActive = isActivePath(subItem.path);

              return (
                <li key={subItem.id}>
                  <button
                    onClick={() => handleItemClick(subItem.path)}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                      isActive
                        ? isGlobalDarkMode
                          ? 'bg-nav-active text-white shadow-md'
                          : 'bg-sidebar-light-active text-gray-900'
                        : isGlobalDarkMode
                          ? 'text-sidebar-foreground/80 hover:text-sidebar-foreground hover:bg-sidebar-accent'
                          : 'text-gray-700 hover:text-gray-900 sidebar-nav-item-hover'
                    }`}
                  >
                    <SubIcon className="w-4 h-4 flex-shrink-0" />
                    <span>{subItem.label}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>
      </aside>
    </>
  );
};
